import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate, Link } from 'react-router-dom';
import { getAlertConfig, updateAlertConfig, SEVERITY_OPTIONS, DELIVERY_MODE_OPTIONS, DAY_OPTIONS } from '../services/alertService';
import { getNotificationChannels, createNotificationChannel, updateNotificationChannel, deleteNotificationChannel, testNotificationChannel } from '../services/driftService';
import { updateOnboardingState, updateProfile } from '../services/profileService';
import { clearTierCache } from '../services/tierService';
import { trackWizardReopen } from '../utils/onboardingAnalytics';
import MFASettings from '../components/Settings/MFASettings';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import { HelpIcon } from '../components/common';
import './Settings.css';

// Notification Channels (Slack / generic webhooks) is unfinished — hidden until shipped.
// Flip to true to re-enable. The separate finished "Alert Notifications" section
// (severity threshold / email pause / delivery mode) is unaffected and stays visible.
// Module-scoped so the load-channels effect can reference it without a deps-array warning.
const SHOW_NOTIFICATION_CHANNELS = false;

// Theme configurations
const THEMES = [
  {
    id: 'deep-ocean',
    name: 'Deep Ocean',
    description: 'Professional navy with aqua accents',
    colors: { bg: '#0a1628', border: '#1a3a5c', accent: '#00D4AA' }
  },
  {
    id: 'bioluminescent',
    name: 'Bioluminescent',
    description: 'Dark depths with electric aqua glow',
    colors: { bg: '#050A14', border: '#1a2d4d', accent: '#00FFD4' }
  },
  {
    id: 'coastal-light',
    name: 'Coastal Light',
    description: 'Clean light mode with ocean blue accents',
    colors: { bg: '#F5F7FA', border: '#CBD5E1', accent: '#0891B2' }
  },
  {
    id: 'arctic-frost',
    name: 'Arctic Frost',
    description: 'Ice-blue tinted light mode',
    colors: { bg: '#F0F4F8', border: '#B0BEC5', accent: '#0077B6' }
  },
  {
    id: 'sunrise-reef',
    name: 'Sunrise Reef',
    description: 'Warm sandy light with coral accents',
    colors: { bg: '#FDF8F3', border: '#D4C4B0', accent: '#E07A5F' }
  },
  {
    id: 'abyssal',
    name: 'Abyssal',
    description: 'Ultra-dark OLED black with muted teal',
    colors: { bg: '#000000', border: '#1E1E1E', accent: '#4ECDC4' }
  },
  {
    id: 'warm',
    name: 'Blade Runner',
    description: 'Warmer dark theme with brown undertones',
    colors: { bg: '#1a1916', border: '#3d3933', accent: '#e63946' }
  },
  {
    id: 'cool',
    name: 'The Grid',
    description: 'Blue-tinted Tron-inspired theme',
    colors: { bg: '#0f1419', border: '#3a4654', accent: '#00d9ff' }
  },
  {
    id: 'neutral',
    name: 'Cyber Punk',
    description: 'Clean neutral grays for enterprise',
    colors: { bg: '#121212', border: '#333333', accent: '#dc3545' }
  },
  {
    id: 'soft',
    name: 'Eclipse',
    description: 'Muted colors inspired by code editors',
    colors: { bg: '#181a1f', border: '#3a3f4a', accent: '#e06c75' }
  }
];

function Settings() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Stripe Customer Portal (PRD-53)
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [planChangeLoading, setPlanChangeLoading] = useState(false);
  const [planChangeError, setPlanChangeError] = useState(null);
  const [planChangeSuccess, setPlanChangeSuccess] = useState(null);

  // Alert configuration state
  const [alertConfig, setAlertConfig] = useState(null);
  const [alertLoading, setAlertLoading] = useState(true);
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertError, setAlertError] = useState(null);
  const [alertSuccess, setAlertSuccess] = useState(null);

  // Notification channels state
  const [notificationChannels, setNotificationChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [newChannel, setNewChannel] = useState({ type: 'email', name: '', emailAddress: '', slackWebhookUrl: '', webhookUrl: '', webhookSecret: '' });
  const [channelSaving, setChannelSaving] = useState(false);
  const [channelTesting, setChannelTesting] = useState(null);
  const [channelTestResult, setChannelTestResult] = useState(null);


  // Theme state
  const [activeTheme, setActiveTheme] = useState(() => {
    return localStorage.getItem('driftalarm-theme') || 'arctic-frost';
  });

  const API_BASE = process.env.REACT_APP_API_URL || 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

  // Get user info from MSAL
  const msalUser = accounts[0] || {};
  const displayName = msalUser.name || msalUser.username || 'User';
  const email = msalUser.username || '';

  const getToken = useCallback(async () => {
    if (!accounts[0]) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0]
      });
      return response.idToken;
    } catch (err) {
      console.error('Token acquisition failed:', err);
      return null;
    }
  }, [instance, accounts]);

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const token = await getToken();
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE}/user-profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setUserProfile(data.profile || {});
        }
      } catch (err) {
        console.error('Failed to load user profile:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [API_BASE, getToken]);

  // Load alert configuration
  useEffect(() => {
    const loadAlertConfig = async () => {
      try {
        setAlertLoading(true);
        const data = await getAlertConfig();
        if (data.success) {
          setAlertConfig(data.config);
        }
      } catch (err) {
        console.error('Failed to load alert config:', err);
        setAlertError('Failed to load alert settings');
      } finally {
        setAlertLoading(false);
      }
    };

    loadAlertConfig();
  }, []);

  // Load notification channels (skipped while the feature is hidden — no pointless GET)
  useEffect(() => {
    if (!SHOW_NOTIFICATION_CHANNELS) { setChannelsLoading(false); return; }
    const loadChannels = async () => {
      try {
        const channels = await getNotificationChannels();
        setNotificationChannels(channels);
      } catch (err) {
        console.error('Failed to load notification channels:', err);
      } finally {
        setChannelsLoading(false);
      }
    };
    loadChannels();
  }, []);

  // Notification channel handlers
  const handleAddChannel = async () => {
    setChannelSaving(true);
    try {
      const result = await createNotificationChannel(newChannel);
      setNotificationChannels(prev => [...prev, result.channel || result]);
      setShowAddChannel(false);
      setNewChannel({ type: 'email', name: '', emailAddress: '', slackWebhookUrl: '', webhookUrl: '', webhookSecret: '' });
    } catch (err) {
      alert(err.message);
    } finally {
      setChannelSaving(false);
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Delete this notification channel?')) return;
    try {
      await deleteNotificationChannel(channelId);
      setNotificationChannels(prev => prev.filter(c => c.channelId !== channelId));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggleChannel = async (channelId, enabled) => {
    try {
      await updateNotificationChannel(channelId, { enabled });
      setNotificationChannels(prev => prev.map(c => c.channelId === channelId ? { ...c, enabled } : c));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleTestChannel = async (channelId) => {
    setChannelTesting(channelId);
    setChannelTestResult(null);
    try {
      const result = await testNotificationChannel(channelId);
      setChannelTestResult({ channelId, ...result });
    } catch (err) {
      setChannelTestResult({ channelId, success: false, error: err.message });
    } finally {
      setChannelTesting(null);
    }
  };

  // Handle alert config change (API call)
  const handleAlertChange = async (updates) => {
    try {
      setAlertSaving(true);
      setAlertError(null);

      const data = await updateAlertConfig(updates);
      if (data.success) {
        setAlertConfig(data.config);
        setAlertSuccess('Settings saved successfully');
        setTimeout(() => setAlertSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Failed to update alert config:', err);
      setAlertError(err.validationErrors?.join(', ') || err.message || 'Failed to save settings');
    } finally {
      setAlertSaving(false);
    }
  };

  // PRD-80 Stage 4: global alarm-severity threshold (userprofile, not alertconfigs).
  const handleSeverityThresholdChange = async (value) => {
    try {
      setAlertSaving(true);
      setAlertError(null);
      await updateProfile({ alarmSeverityThreshold: value });
      setUserProfile(prev => ({ ...(prev || {}), alarmSeverityThreshold: value }));
      setAlertSuccess('Alarm threshold saved');
      setTimeout(() => setAlertSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to update alarm threshold:', err);
      setAlertError(err.message || 'Failed to save alarm threshold');
    } finally {
      setAlertSaving(false);
    }
  };

  // PRD-80 Stage 4: pause all outbound emails for 24h ('' clears the pause).
  const alertsPaused = !!(userProfile?.alertsPausedUntil
    && new Date(userProfile.alertsPausedUntil).getTime() > Date.now());

  const handlePauseToggle = async () => {
    const next = alertsPaused ? '' : new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      setAlertSaving(true);
      setAlertError(null);
      await updateProfile({ alertsPausedUntil: next });
      setUserProfile(prev => ({ ...(prev || {}), alertsPausedUntil: next }));
      setAlertSuccess(next ? 'All emails paused for 24h' : 'Email pause cleared');
      setTimeout(() => setAlertSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to update email pause:', err);
      setAlertError(err.message || 'Failed to update email pause');
    } finally {
      setAlertSaving(false);
    }
  };

  // Handle theme change
  const handleThemeChange = (themeId) => {
    document.documentElement.classList.add('theme-transitioning');
    setActiveTheme(themeId);
    document.documentElement.setAttribute('data-theme', themeId);
    localStorage.setItem('driftalarm-theme', themeId);
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transitioning');
    }, 300);
  };


  const userTier = userProfile?.tier || 'trial';

  const handleManageSubscription = async () => {
    setPortalError(null);
    setPortalLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        setPortalError('Please log in again to manage your subscription.');
        setPortalLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE}/stripe-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Could not open billing portal (${response.status}).`);
      }
      const data = await response.json();
      if (!data.url) throw new Error('Billing portal URL missing.');
      window.location.href = data.url;
    } catch (err) {
      console.error('Manage subscription error:', err);
      setPortalError(err.message);
      setPortalLoading(false);
    }
  };

  // PRD-146 WP2 — self-serve Standard<->Pro plan change (no email-to-sales).
  // Switches to the OTHER paid tier on the same billing interval (use the
  // billing portal to also change interval). The tier write lands via the
  // Stripe subscription.updated webhook; we clear the tier cache + reload the
  // profile so the new plan reflects without waiting on the ~60s cache window.
  const handleChangePlan = async (targetTier) => {
    setPlanChangeError(null);
    setPlanChangeSuccess(null);
    setPlanChangeLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        setPlanChangeError('Please log in again to change your plan.');
        setPlanChangeLoading(false);
        return;
      }
      const interval = (userProfile?.billingInterval === 'year') ? 'annual' : 'monthly';
      const lookupKey = `${targetTier}_${interval}`;
      const response = await fetch(`${API_BASE}/stripe-upgrade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lookupKey }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Could not change plan (${response.status}).`);
      }
      clearTierCache();
      setPlanChangeSuccess(
        data.changed === false
          ? `You're already on the ${targetTier === 'pro' ? 'Pro' : 'Standard'} plan.`
          : `Plan changed to ${targetTier === 'pro' ? 'Pro' : 'Standard'}. Prorated charges apply on your next invoice.`
      );
      // Reload the profile so the tier badge reflects the change.
      try {
        const refreshed = await fetch(`${API_BASE}/user-profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (refreshed.ok) {
          const profileData = await refreshed.json();
          setUserProfile(profileData.profile || {});
        }
      } catch { /* badge refresh is best-effort */ }
    } catch (err) {
      console.error('Change plan error:', err);
      setPlanChangeError(err.message);
    } finally {
      setPlanChangeLoading(false);
    }
  };

  return (
    <CleanModernLayout>
    <div className="settings-page">
      <div className="settings-header">
        <h1>
          <span className="settings-header-icon">⚙️</span>
          Settings
        </h1>
        <p className="subtitle">Manage your account and preferences</p>
      </div>

      <div className="settings-content">
        {/* Profile Section */}
        <section className="settings-section">
          <h2 className="section-title">
            <span className="section-icon">👤</span>
            Profile
          </h2>
          <div className="settings-card">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <span>Loading profile...</span>
              </div>
            ) : (
              <div className="profile-info">
                <div className="profile-avatar">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div className="profile-details">
                  <div className="profile-field">
                    <label>Name</label>
                    <span>{displayName}</span>
                  </div>
                  <div className="profile-field">
                    <label>Email</label>
                    <span>{email}</span>
                  </div>
                  <div className="profile-field">
                    <label>Subscription</label>
                    <span className={`tier-badge ${userTier}`}>
                      {userTier.toUpperCase()}
                    </span>
                  </div>
                  {(userTier === 'standard' || userTier === 'pro') && (
                    <div className="profile-field">
                      <label>Billing</label>
                      <button
                        type="button"
                        onClick={handleManageSubscription}
                        disabled={portalLoading}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--accent-primary, #0077B6)',
                          background: 'transparent',
                          color: 'var(--accent-primary, #0077B6)',
                          cursor: portalLoading ? 'wait' : 'pointer',
                          fontFamily: 'var(--font-body)',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                        }}
                      >
                        {portalLoading ? 'Opening…' : 'Manage Subscription'}
                      </button>
                      {portalError && (
                        <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '8px', maxWidth: '400px' }}>
                          {portalError}
                        </p>
                      )}
                    </div>
                  )}
                  {(userTier === 'standard' || userTier === 'pro') && (
                    <div className="profile-field">
                      <label>Plan</label>
                      <div>
                        <button
                          type="button"
                          onClick={() => handleChangePlan(userTier === 'standard' ? 'pro' : 'standard')}
                          disabled={planChangeLoading}
                          style={{
                            padding: '8px 16px',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--accent-primary, #0077B6)',
                            background: userTier === 'standard' ? 'var(--accent-primary, #0077B6)' : 'transparent',
                            color: userTier === 'standard' ? '#fff' : 'var(--accent-primary, #0077B6)',
                            cursor: planChangeLoading ? 'wait' : 'pointer',
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                          }}
                        >
                          {planChangeLoading
                            ? 'Updating…'
                            : userTier === 'standard' ? 'Upgrade to Pro' : 'Switch to Standard'}
                        </button>
                        <p style={{ color: 'var(--text-muted, #78909C)', fontSize: '0.75rem', marginTop: '6px', maxWidth: '420px' }}>
                          Self-serve — changes apply immediately with prorated billing. Use Manage Subscription to change billing interval.
                        </p>
                        {planChangeSuccess && (
                          <p style={{ color: '#16A34A', fontSize: '0.8rem', marginTop: '4px', maxWidth: '420px' }}>
                            {planChangeSuccess}
                          </p>
                        )}
                        {planChangeError && (
                          <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', maxWidth: '420px' }}>
                            {planChangeError}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Appearance Section */}
        <section className="settings-section">
          <h2 className="section-title">
            <span className="section-icon">🎨</span>
            Appearance
          </h2>
          <div className="settings-card">
            <p className="theme-description">
              Choose a theme for your dashboard experience
            </p>
            <div className="theme-grid">
              {THEMES.map((theme) => (
                <button
                  key={theme.id}
                  className={`theme-option ${activeTheme === theme.id ? 'active' : ''}`}
                  onClick={() => handleThemeChange(theme.id)}
                >
                  <div
                    className="theme-preview"
                    style={{
                      background: theme.colors.bg,
                      borderColor: theme.colors.border
                    }}
                  >
                    <div
                      className="theme-accent-bar"
                      style={{ background: theme.colors.accent }}
                    />
                    <div className="theme-preview-lines">
                      <div className="preview-line" style={{ background: theme.colors.border }} />
                      <div className="preview-line short" style={{ background: theme.colors.border }} />
                    </div>
                  </div>
                  <div className="theme-info">
                    <span className="theme-name">{theme.name}</span>
                    <span className="theme-desc">{theme.description}</span>
                  </div>
                  {activeTheme === theme.id && (
                    <span className="theme-active-badge">Active</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Security Section */}
        <section className="settings-section">
          <h2 className="section-title">
            <span className="section-icon">🔐</span>
            Security
          </h2>
          <div className="settings-card">
            <MFASettings />
          </div>
        </section>

        {/* Alert Notifications Section */}
        <section className="settings-section">
          <h2 className="section-title">
            <span className="section-icon">🔔</span>
            Alert Notifications
          </h2>

          {alertError && (
            <div className="alert-banner alert-error">
              <span>!</span> {alertError}
              <button onClick={() => setAlertError(null)}>x</button>
            </div>
          )}

          {alertSuccess && (
            <div className="alert-banner alert-success">
              <span>ok</span> {alertSuccess}
            </div>
          )}

          {alertLoading ? (
            <div className="settings-card">
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <span>Loading alert settings...</span>
              </div>
            </div>
          ) : alertConfig ? (
            <>
              {/* Alarm & email controls (PRD-80 Stage 4) — per-rule alarm/email toggles live on /drift-rules */}
              <div className="settings-card alert-config-card">
                <div className="alert-config-header">
                  <h3 className="config-title">Alarm &amp; Email Controls</h3>
                  <p className="config-description">
                    Set how sensitive your alerts are, and pause email if you need quiet.
                    Turn individual alerts and their emails on or off on the{' '}
                    <a href="/drift-rules">Alert Rules</a> page.
                  </p>
                </div>
                <div className="alert-config-options">
                  <div className="config-field">
                    <label>Alarm me at severity</label>
                    <select
                      value={userProfile?.alarmSeverityThreshold || 'medium'}
                      onChange={(e) => handleSeverityThresholdChange(e.target.value)}
                      disabled={alertSaving}
                    >
                      {SEVERITY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="config-field">
                    <label>Email delivery</label>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handlePauseToggle}
                      disabled={alertSaving}
                    >
                      {alertsPaused ? 'Resume emails' : 'Pause all emails for 24h'}
                    </button>
                    {alertsPaused && (
                      <p className="config-description" style={{ marginTop: '8px' }}>
                        Emails paused until {new Date(userProfile.alertsPausedUntil).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery Mode */}
              <div className="settings-card alert-config-card">
                <div className="alert-config-header">
                  <h3 className="config-title">Delivery Mode</h3>
                  <p className="config-description">Choose how you want to receive alerts</p>
                </div>
                <div className="alert-config-options">
                  <div className="config-field">
                    <label>Delivery mode</label>
                    <select
                      value={alertConfig.delivery?.mode || 'immediate'}
                      onChange={(e) => handleAlertChange({ deliveryMode: e.target.value })}
                      disabled={alertSaving}
                    >
                      {DELIVERY_MODE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  {(alertConfig.delivery?.mode === 'daily' || alertConfig.delivery?.mode === 'weekly') && (
                    <div className="config-field">
                      <label>Digest time</label>
                      <input
                        type="time"
                        value={alertConfig.delivery?.digestTime || '09:00'}
                        onChange={(e) => handleAlertChange({ digestTime: e.target.value })}
                        disabled={alertSaving}
                      />
                    </div>
                  )}
                  {alertConfig.delivery?.mode === 'weekly' && (
                    <div className="config-field">
                      <label>Day of week</label>
                      <select
                        value={alertConfig.delivery?.digestDay ?? 1}
                        onChange={(e) => handleAlertChange({ digestDay: parseInt(e.target.value) })}
                        disabled={alertSaving}
                      >
                        {DAY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="settings-card">
              <p className="error-text">Failed to load alert configuration</p>
            </div>
          )}
        </section>

        {/* Drift Alarms Section — link to dedicated page */}
        <section className="settings-section">
          <h2 className="section-title">
            <span className="section-icon">⚡</span>
            Drift Alarms
          </h2>
          <div className="settings-card">
            <p className="config-description">
              Configure drift alarm rules and rule packs that trigger when your attack surface changes.
            </p>
            <Link to="/drift-alarms" className="cm-action-btn" style={{ marginTop: '8px', display: 'inline-block' }}>
              Manage Drift Alarms
            </Link>
          </div>
        </section>

        {/* Notification Channels Section — hidden while unfinished (SHOW_NOTIFICATION_CHANNELS) */}
        {SHOW_NOTIFICATION_CHANNELS && (
        <section className="settings-section">
          <h2 className="section-title">
            <span className="section-icon">🔔</span>
            Notification Channels
            <HelpIcon content="Choose how you want to be notified when drift alarms trigger. You can route different severity levels to different channels." category="context" />
          </h2>

          {channelsLoading ? (
            <div className="settings-card">
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <span>Loading notification channels...</span>
              </div>
            </div>
          ) : (
            <>
              <p className="section-description" style={{ marginBottom: '1rem', color: 'var(--text-secondary, #888)', fontSize: '0.9rem' }}>
                Configure where drift alarms are delivered. Channels receive notifications when drift rules trigger.
              </p>

              {notificationChannels.length === 0 && !showAddChannel && (
                <div className="settings-card" style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: 'var(--text-secondary, #888)', marginBottom: '1rem' }}>
                    No notification channels configured. Add one to receive drift alerts.
                  </p>
                </div>
              )}

              {notificationChannels.map(channel => (
                <div key={channel.channelId} className="settings-card drift-channel-card">
                  <div className="drift-channel-header">
                    <span className="drift-channel-icon">
                      {channel.type === 'email' ? '✉' : channel.type === 'slack' ? '#' : '⚡'}
                    </span>
                    <div className="drift-channel-info">
                      <span className="drift-channel-name">{channel.name}</span>
                      <span className="drift-channel-type">{channel.type.toUpperCase()}</span>
                      {channel.type === 'email' && channel.emailAddress && (
                        <span className="drift-channel-detail">{channel.emailAddress}</span>
                      )}
                      {channel.type === 'slack' && (
                        <span className="drift-channel-detail">Slack webhook configured</span>
                      )}
                      {channel.type === 'webhook' && channel.webhookUrl && (
                        <span className="drift-channel-detail">{channel.webhookUrl}</span>
                      )}
                    </div>
                    <div className="drift-channel-actions">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={channel.enabled !== false}
                          onChange={(e) => handleToggleChannel(channel.channelId, e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <button
                        className="cm-action-btn"
                        onClick={() => handleTestChannel(channel.channelId)}
                        disabled={channelTesting === channel.channelId}
                      >
                        {channelTesting === channel.channelId ? 'Testing...' : 'Test'}
                      </button>
                      <button
                        className="cm-action-btn danger"
                        onClick={() => handleDeleteChannel(channel.channelId)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {channelTestResult?.channelId === channel.channelId && (
                    <div className={`drift-test-result ${channelTestResult.success ? 'success' : 'failure'}`}>
                      {channelTestResult.success
                        ? `Test successful${channelTestResult.responseTime ? ` (${channelTestResult.responseTime}ms)` : ''}`
                        : `Test failed: ${channelTestResult.error || 'Unknown error'}`}
                    </div>
                  )}
                  {channel.webhookLastTestedAt && (
                    <div className="drift-channel-meta">Last tested: {new Date(channel.webhookLastTestedAt).toLocaleString()}</div>
                  )}
                </div>
              ))}

              {showAddChannel ? (
                <div className="settings-card drift-add-channel-form">
                  <h4 style={{ margin: '0 0 1rem', color: 'var(--text-primary, #f0f0f0)' }}>Add Notification Channel</h4>
                  <div className="drift-form-group">
                    <label>Channel Type</label>
                    <select
                      value={newChannel.type}
                      onChange={(e) => setNewChannel({ ...newChannel, type: e.target.value })}
                      className="drift-select"
                    >
                      <option value="email">Email</option>
                      <option value="slack">Slack</option>
                      <option value="webhook">Webhook</option>
                    </select>
                  </div>
                  <div className="drift-form-group">
                    <label>Channel Name</label>
                    <input
                      type="text"
                      value={newChannel.name}
                      onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value })}
                      placeholder="e.g., Security Team Email"
                      className="drift-input"
                    />
                  </div>
                  {newChannel.type === 'email' && (
                    <div className="drift-form-group">
                      <label>Email Address</label>
                      <input
                        type="email"
                        value={newChannel.emailAddress}
                        onChange={(e) => setNewChannel({ ...newChannel, emailAddress: e.target.value })}
                        placeholder="alerts@example.com"
                        className="drift-input"
                      />
                    </div>
                  )}
                  {newChannel.type === 'slack' && (
                    <div className="drift-form-group">
                      <label>Slack Webhook URL</label>
                      <input
                        type="url"
                        value={newChannel.slackWebhookUrl}
                        onChange={(e) => setNewChannel({ ...newChannel, slackWebhookUrl: e.target.value })}
                        placeholder="https://hooks.slack.com/services/..."
                        className="drift-input"
                      />
                      <p className="drift-form-hint">Create an Incoming Webhook in your Slack workspace settings</p>
                    </div>
                  )}
                  {newChannel.type === 'webhook' && (
                    <>
                      <div className="drift-form-group">
                        <label>Webhook URL</label>
                        <input
                          type="url"
                          value={newChannel.webhookUrl}
                          onChange={(e) => setNewChannel({ ...newChannel, webhookUrl: e.target.value })}
                          placeholder="https://your-service.com/webhook"
                          className="drift-input"
                        />
                      </div>
                      <div className="drift-form-group">
                        <label>HMAC Secret (optional)</label>
                        <input
                          type="password"
                          value={newChannel.webhookSecret}
                          onChange={(e) => setNewChannel({ ...newChannel, webhookSecret: e.target.value })}
                          placeholder="Optional signing secret"
                          className="drift-input"
                        />
                        <p className="drift-form-hint">Payloads signed with HMAC-SHA256 in X-DriftAlarm-Signature header</p>
                      </div>
                    </>
                  )}
                  <div className="drift-form-actions">
                    <button
                      className="cm-action-btn primary"
                      onClick={handleAddChannel}
                      disabled={channelSaving || !newChannel.name}
                    >
                      {channelSaving ? 'Saving...' : 'Save Channel'}
                    </button>
                    <button
                      className="cm-action-btn"
                      onClick={() => { setShowAddChannel(false); setNewChannel({ type: 'email', name: '', emailAddress: '', slackWebhookUrl: '', webhookUrl: '', webhookSecret: '' }); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="cm-action-btn"
                  onClick={() => setShowAddChannel(true)}
                  style={{ marginTop: '8px' }}
                >
                  + Add Channel
                </button>
              )}
            </>
          )}
        </section>
        )}

        {/* Weekly Trend Reports Section */}
        <section className="settings-section">
          <h2 className="section-title">
            <span className="section-icon">📊</span>
            Weekly Trend Reports
          </h2>

          {alertLoading ? (
            <div className="settings-card">
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <span>Loading settings...</span>
              </div>
            </div>
          ) : alertConfig ? (
            <>
              {/* Weekly Report Toggle */}
              <div className="settings-card alert-config-card">
                <div className="alert-config-header">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={alertConfig.weeklyReport?.enabled !== false}
                      onChange={(e) => handleAlertChange({ weeklyReportEnabled: e.target.checked })}
                      disabled={alertSaving}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">Weekly AI Trend Reports</span>
                  </label>
                  <p className="config-description">
                    Receive AI-powered weekly security trend analysis with actionable recommendations,
                    pentest roadmap, and compliance mapping
                  </p>
                </div>
                {(alertConfig.weeklyReport?.enabled !== false) && (
                  <div className="alert-config-options">
                    <div className="config-field">
                      <label>Generate reports on</label>
                      <select
                        value={alertConfig.weeklyReport?.day ?? 0}
                        onChange={(e) => handleAlertChange({ weeklyReportDay: parseInt(e.target.value) })}
                        disabled={alertSaving}
                      >
                        {DAY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="config-field">
                      <label className="toggle-label inline">
                        <input
                          type="checkbox"
                          checked={alertConfig.weeklyReport?.emailEnabled !== false}
                          onChange={(e) => handleAlertChange({ weeklyReportEmailEnabled: e.target.checked })}
                          disabled={alertSaving}
                        />
                        <span className="toggle-slider small"></span>
                        <span className="toggle-text">Send email summary</span>
                      </label>
                    </div>
                    <div className="config-field">
                      <label className="toggle-label inline">
                        <input
                          type="checkbox"
                          checked={alertConfig.weeklyReport?.includeAccount !== false}
                          onChange={(e) => handleAlertChange({ weeklyReportIncludeAccount: e.target.checked })}
                          disabled={alertSaving}
                        />
                        <span className="toggle-slider small"></span>
                        <span className="toggle-text">Include portfolio-level rollup</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Weekly Report Features Info */}
              <div className="settings-card info-card">
                <h3 className="info-title">What's included in Weekly Reports</h3>
                <div className="feature-preview-list">
                  <div className="preview-item">
                    <span className="check active">X</span>
                    <span>Executive summary with risk posture</span>
                  </div>
                  <div className="preview-item">
                    <span className="check active">X</span>
                    <span>Prioritized actionable recommendations</span>
                  </div>
                  <div className="preview-item">
                    <span className="check active">X</span>
                    <span>Pentest roadmap (immediate + 30-day targets)</span>
                  </div>
                  <div className="preview-item">
                    <span className="check active">X</span>
                    <span>Compliance mapping (OWASP, CIS, NIST)</span>
                  </div>
                  <div className="preview-item">
                    <span className="check active">X</span>
                    <span>Recommendation tracking across weeks</span>
                  </div>
                  <div className="preview-item">
                    <span className="check active">X</span>
                    <span>4-week historical trend analysis</span>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </section>


        {/* Onboarding Section */}
        <section className="settings-section">
          <h2 className="section-title">
            <span className="section-icon">🚀</span>
            Onboarding
          </h2>
          <div className="settings-card">
            <p className="config-description" style={{ marginBottom: '12px' }}>
              Re-run the setup wizard to add another asset or review your configuration.
            </p>
            <button
              className="cm-action-btn"
              onClick={async () => {
                // PRD-64: server-side replay path replaces localStorage flag.
                // Legacy removes left as a one-release no-op safety net.
                localStorage.removeItem('driftalarm_onboardingComplete');
                localStorage.removeItem('driftalarm_onboardingStep');
                localStorage.removeItem('driftalarm_onboardingAssetId');
                try {
                  await updateOnboardingState({
                    completedAt: null,
                    dismissedAt: null,
                    lastStep: 1,
                    closedFromStep: null,
                    source: 'reopen',
                    firstScansBannerDismissedAt: null,  // PRD-64 WP3 FollowUp Part D — let banner reappear if scans haven't run
                  });
                  trackWizardReopen('settings');
                } catch (err) {
                  console.error('Failed to reopen onboarding wizard:', err);
                }
                // reopenWizard bypasses the Command Center's asset-first gate so an
                // existing-asset user can re-open the wizard to add another asset.
                navigate('/command-center', { state: { reopenWizard: true } });
              }}
            >
              Re-run setup wizard
            </button>
          </div>

          {/* Onboarding Email Preferences */}
          <div className="settings-card alert-config-card" style={{ marginTop: '12px' }}>
            <div className="alert-config-header">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={!(userProfile?.onboardingEmailsOptOut)}
                  onChange={async (e) => {
                    const optOut = !e.target.checked;
                    try {
                      const token = await getToken();
                      if (!token) return;
                      const response = await fetch(`${API_BASE}/user-profile-update`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ onboardingEmailsOptOut: optOut })
                      });
                      if (response.ok) {
                        setUserProfile(prev => ({ ...prev, onboardingEmailsOptOut: optOut }));
                      }
                    } catch (err) {
                      console.error('Failed to update email preference:', err);
                    }
                  }}
                  disabled={loading}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">Receive onboarding and trial emails</span>
              </label>
              <p className="config-description">
                Helpful tips, scan summaries, and trial reminders sent during your first 30 days
              </p>
            </div>
          </div>
        </section>
      </div>

    </div>
    </CleanModernLayout>
  );
}

export default Settings;
